import React, { useState, useEffect } from "react";
import {
    Modal, ModalHeader, ModalBody, Button, Form, FormGroup,
    Label, Input, Card, CardBody, CardHeader, Row, Col, FormText, Collapse,
    Spinner, Alert
} from "reactstrap";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { getMasterSheetLayout } from "../../TenantForm/Helpers/api";
import { MetaFields } from "./Meta/MetaFields";

import { Leases } from "./Leases";
import { Documents } from "./AdditionalDocuments/Documents";
import { Pictures } from "./AdditionalDocuments/Components/Pictures";
import { Memos } from "./AdditionalDocuments/Memos";
import { PadInfo } from "../../TenantForm/Helpers/PadInfo/PadInfo";

const AccordionContext = React.createContext({
    openGroups: {},
    toggleGroup: () => { }
});

const safeFormatDate = (date) => {
    if (!date) return "";
    try {
        const dateObj = new Date(date);
        return !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : "";
    } catch (error) {
        console.error("Error formatting date:", error);
        return "";
    }
};

const getDisplayValue = (value, field) => {
    if (value === null || value === undefined) return '';
    if (field.type === 'number' && typeof value === 'number') {
        return `$${value.toFixed(2)}`;
    }
    return value;
};

const getNestedValue = (obj, path) => {
    if (!obj) return undefined;
    const keys = path.split('.');
    return keys.reduce((o, key) => {
        if (o && typeof o === 'object') {
            if (key.includes('[') && key.includes(']')) {
                const arrayKey = key.split('[')[0];
                const index = parseInt(key.split('[')[1].split(']')[0]);
                return o[arrayKey] ? o[arrayKey][index] : undefined;
            }
            return o[key];
        }
        return undefined;
    }, obj);
};

const DynamicField = ({ field, name, values, isNested = false }) => {
    const value = getNestedValue(values, name);
    const isFeeField = field.key?.toLowerCase().includes('fee') ||
        field.key?.toLowerCase().includes('sd') ||
        field.key?.toLowerCase().includes('targetrent') ||
        field.key?.toLowerCase().includes('basicrent');
    const { openGroups, toggleGroup } = React.useContext(AccordionContext);

    const calculateResidentCount = () => {
        const fieldValue = getNestedValue(values, name);
        let tenantCount = 0;

        if (field.type === 'array') {
            tenantCount = Array.isArray(fieldValue) ? fieldValue.length : 0;
            return {
                count: tenantCount,
                explanation: `Calculated as: ${tenantCount} tenant(s) from the array`
            };
        } else if (field.type === 'text') {
            if (typeof fieldValue === 'string' && fieldValue.trim()) {
                const separators = /,|\band\b|&/;
                const tenants = fieldValue.split(separators).map(name => name.trim()).filter(name => name.length > 0);
                tenantCount = tenants.length;
            }
            return {
                count: tenantCount,
                explanation: `${tenantCount} tenant(s) (split by commas, 'and', or '&')`
            };
        }

        return {
            count: 0,
            explanation: `0 tenant(s) (no valid tenant data)`
        };
    };

    const isTenantField = field.key === 'tenants' || field.key === 'tenant';
    const residentInfo = isTenantField ? calculateResidentCount() : null;

    switch (field.type) {
        case "text":
        case "email":
        case "status":
            return (
                <FormGroup className={isNested ? "mb-2" : "mb-3"}>
                    <Label for={name}>
                        {field.label}
                        {isTenantField && (
                            <>
                                <span className="ms-2 text-muted">
                                    (Residents: {residentInfo.count})
                                </span>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id={`tooltip-${name.replace(/\./g, '-')}`} style={{ zIndex: 1050 }}>
                                            {residentInfo.explanation}
                                        </Tooltip>
                                    }
                                >
                                    <span className="ms-1" style={{ cursor: 'pointer' }}>
                                        <FeatherIcon icon="info" size={16} className="text-muted" />
                                    </span>
                                </OverlayTrigger>
                            </>
                        )}
                    </Label>
                    <Input
                        id={name}
                        type="text"
                        value={isFeeField ? `$${parseFloat(value || 0).toFixed(2)}` : value || ''}
                        disabled={true}
                        readOnly={true}
                    />
                    <FormText>This field is read-only (archived data preview).</FormText>
                </FormGroup>
            );

        case "number":
            return (
                <FormGroup className={isNested ? "mb-2" : "mb-3"}>
                    <Label for={name}>{field.label}</Label>
                    <Input
                        id={name}
                        type="text"
                        value={isFeeField ? `$${parseFloat(value || 0).toFixed(2)}` : value || ''}
                        disabled={true}
                        readOnly={true}
                    />
                    <FormText>This field is read-only (archived data preview).</FormText>
                </FormGroup>
            );

        case "date":
            return (
                <FormGroup className={isNested ? "mb-2" : "mb-3"}>
                    <Label for={name}>{field.label}</Label>
                    <Input
                        id={name}
                        type="text"
                        value={safeFormatDate(value)}
                        disabled={true}
                        readOnly={true}
                    />
                    <FormText>This field is read-only (archived data preview).</FormText>
                </FormGroup>
            );

        case "select":
            const displayValue = typeof value === 'object' && value?.label
                ? value.label
                : value || '';
            return (
                <FormGroup className={isNested ? "mb-2" : "mb-3"}>
                    <Label for={name}>{field.label}</Label>
                    <Input
                        id={name}
                        type="text"
                        value={displayValue}
                        disabled={true}
                        readOnly={true}
                    />
                    <FormText>This field is read-only (archived data preview).</FormText>
                </FormGroup>
            );

        case "boolean":
            return (
                <FormGroup check className={isNested ? "mb-2" : "mb-3"}>
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={value || false}
                            disabled={true}
                            readOnly={true}
                        />
                        {field.label}
                    </Label>
                    <FormText>This field is read-only (archived data preview).</FormText>
                </FormGroup>
            );

        case "group":
            return (
                <Card className={isNested ? "mb-2" : "mb-3"}>
                    <CardHeader className="py-3 text-center">
                        <Button
                            color="primary"
                            outline
                            className="fw-bold px-4 py-2 rounded-pill"
                            onClick={() => toggleGroup(name)}
                        >
                            <FeatherIcon icon="folder" className="me-2" size={16} />
                            {field.label}
                        </Button>
                    </CardHeader>
                    <Collapse isOpen={openGroups[name] !== false}>
                        <CardBody>
                            {field.description && (
                                <p className="text-muted small mb-3">{field.description}</p>
                            )}
                            <Row>
                                {field.fields.map((subField, index) => (
                                    <Col md={6} key={index}>
                                        <DynamicField
                                            field={subField}
                                            name={`${name}.${subField.key}`}
                                            values={values}
                                            isNested={true}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </CardBody>
                    </Collapse>
                </Card>
            );

        case "array":
            return (
                <Card className={isNested ? "mb-2" : "mb-3"}>
                    <CardHeader className="py-3 text-center">
                        <Button
                            color="primary"
                            outline
                            className="fw-bold px-4 py-2 rounded-pill"
                            onClick={() => toggleGroup(name)}
                        >
                            <FeatherIcon icon="list" className="me-2" size={16} />
                            {field.label}
                            {isTenantField && (
                                <span className="ms-2 text-muted">
                                    (Residents: {residentInfo.count})
                                </span>
                            )}
                        </Button>
                    </CardHeader>
                    <Collapse isOpen={openGroups[name] !== false}>
                        <CardBody>
                            {field.description && (
                                <p className="text-muted small mb-3">{field.description}</p>
                            )}
                            {value?.map((item, index) => (
                                <Card key={index} className="mb-3">
                                    <CardHeader className="py-3 text-center">
                                        <Button
                                            color="primary"
                                            outline
                                            className="fw-bold px-4 py-2 rounded-pill"
                                            onClick={() => toggleGroup(`${name}[${index}]`)}
                                        >
                                            <FeatherIcon icon="list" className="me-2" size={16} />
                                            {field.label} #{index + 1}
                                        </Button>
                                    </CardHeader>
                                    <Collapse isOpen={openGroups[`${name}[${index}]`] !== false}>
                                        <CardBody>
                                            <Row>
                                                {field.fields.map((subField, fieldIndex) => (
                                                    <Col md={subField.type === 'group' ? 12 : 6} key={fieldIndex}>
                                                        <DynamicField
                                                            field={subField}
                                                            name={`${name}[${index}].${subField.key}`}
                                                            values={values}
                                                            isNested={true}
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                        </CardBody>
                                    </Collapse>
                                </Card>
                            ))}
                            {isTenantField && (
                                <FormText>{residentInfo.explanation}</FormText>
                            )}
                        </CardBody>
                    </Collapse>
                </Card>
            );

        default:
            return (
                <FormGroup className={isNested ? "mb-2" : "mb-3"}>
                    <Label for={name}>{field.label}</Label>
                    <Input
                        id={name}
                        value={getDisplayValue(value, field)}
                        disabled={true}
                        readOnly={true}
                    />
                    <FormText>This field is read-only (archived data preview).</FormText>
                </FormGroup>
            );
    }
};

const convertApiToFormValues = (apiData, formLayout) => {
    if (!apiData) return null;

    const formValues = {};

    const parseDate = (value) => {
        if (!value) return null;
        const isoDate = new Date(value);
        if (!isNaN(isoDate.getTime())) return isoDate;
        const dmyMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
        if (dmyMatch) {
            const [, day, month, year] = dmyMatch;
            const parsed = new Date(`${year}-${month}-${day}`);
            return !isNaN(parsed.getTime()) ? parsed : null;
        }
        return null;
    };

    const processField = (field, apiValue) => {
        if (apiValue === undefined || apiValue === null) return null;

        if (field.type === "array") {
            return Array.isArray(apiValue)
                ? apiValue.map(item => {
                    const arrayItemValues = {};
                    if (field.fields) {
                        field.fields.forEach(subField => {
                            arrayItemValues[subField.key] = processField(subField, item[subField.key]);
                        });
                    }
                    return arrayItemValues;
                })
                : [];
        }

        if (field.type === "group") {
            const groupValues = {};
            if (field.fields) {
                field.fields.forEach(subField => {
                    groupValues[subField.key] = processField(subField, apiValue[subField.key]);
                });
            }
            return groupValues;
        }

        if (field.type === "select") {
            if (apiValue && typeof apiValue === 'string') {
                return { value: String(apiValue), label: String(apiValue) };
            } else if (typeof apiValue === 'number') {
                return { value: String(apiValue), label: String(apiValue) };
            }
            return apiValue;
        }

        if (field.type === "date") {
            return parseDate(apiValue);
        }

        return apiValue;
    };

    formValues['meta'] = {
        accounts: {
            heat: apiData.meta?.accounts?.heat || "",
            water: apiData.meta?.accounts?.water || "",
            electricity: apiData.meta?.accounts?.electricity || "",
        },
        parking: Array.isArray(apiData.meta?.parking) ? apiData.meta.parking : [],
        storage: Array.isArray(apiData.meta?.storage) ? apiData.meta.storage : [],
        fobs: Array.isArray(apiData.meta?.fobs) ? apiData.meta.fobs : [],
        keys: Array.isArray(apiData.meta?.keys) ? apiData.meta.keys : [],
        occupants: Array.isArray(apiData.meta?.occupants) ? apiData.meta.occupants : []
    };

    formValues['documentsNA'] = apiData.documentsNA || {};

    formLayout.forEach(field => {
        formValues[field.key] = processField(field, apiData[field.key]);
    });

    return formValues;
};

const PreviewTenantForm = ({
    show,
    handleClose,
    propertyId,
    propertyName = "",
    buildingName = null,
    previewData = null,
    tenantInfo
}) => {
    const buildTenantInfoString = (tenantData = {}) => {
        const unitNo = tenantData?.unitNo || 'N/A';
        const address = buildingName || 'N/A';
        const tenantName = tenantData?.tenants || tenantData?.tenant || 'N/A';
        const fullAddress = `${propertyName !== 'N/A' ? propertyName + ' ' : ''}${address}`;

        return (
            <div className="text-center">
                <h5 className="mb-3 fw-bold text-primary">{tenantName}</h5>
                <div className="d-flex justify-content-center align-items-center text-muted fs-6">
                    <FeatherIcon icon="home" className="me-2 text-primary" size={18} />
                    <span>Unit No. {unitNo}</span>
                    <FeatherIcon icon="map-pin" className="ms-3 me-2 text-primary" size={18} />
                    <span>Address: {fullAddress}</span>
                </div>
            </div>
        );
    };

    const [formLayout, setFormLayout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formValues, setFormValues] = useState(null);
    const [openGroups, setOpenGroups] = useState({});

    const toggleGroup = (groupId, forceOpen) => {
        setOpenGroups(prev => ({
            ...prev,
            [groupId]: forceOpen !== undefined ? forceOpen : !prev[groupId]
        }));
    };

    useEffect(() => {
        const fetchLayout = async () => {
            try {
                setLoading(true);
                const layoutData = await getMasterSheetLayout(propertyId, buildingName);

                if (layoutData && layoutData.fields) {
                    setFormLayout(layoutData.fields);

                    const initialAccordionState = {};
                    initialAccordionState['basicFields'] = false;
                    initialAccordionState['meta'] = false;
                    initialAccordionState['general'] = false;

                    const initializeAccordions = (fields, parentPath = '') => {
                        fields.forEach(field => {
                            if (field.type === 'group') {
                                const fieldPath = parentPath ? `${parentPath}.${field.key}` : field.key;
                                initialAccordionState[fieldPath] = true;
                                if (field.fields) {
                                    initializeAccordions(field.fields, fieldPath);
                                }
                            } else if (field.type === 'array' && field.fields) {
                                field.fields.forEach(arrayField => {
                                    if (arrayField.type === 'group' && arrayField.fields) {
                                        initializeAccordions([arrayField], `${field.key}[0]`);
                                    }
                                });
                            }
                        });
                    };

                    initializeAccordions(layoutData.fields);
                    setOpenGroups(initialAccordionState);

                    if (previewData) {
                        const convertedValues = convertApiToFormValues(previewData, layoutData.fields);
                        setFormValues(convertedValues);
                    }
                } else {
                    setError("Failed to load form layout");
                }
            } catch (err) {
                setError("Error loading form layout: " + err);
            } finally {
                setLoading(false);
            }
        };

        if (show && propertyId) {
            fetchLayout();
        }
    }, [propertyId, buildingName, show, previewData]);

    if (loading) {
        return (
            <Modal isOpen={show} toggle={handleClose} size="xl">
                <ModalHeader toggle={handleClose}>
                    Loading Form...
                </ModalHeader>
                <ModalBody className="text-center p-5">
                    <Spinner />
                </ModalBody>
            </Modal>
        );
    }

    if (error) {
        return (
            <Modal isOpen={show} toggle={handleClose} size="xl">
                <ModalHeader toggle={handleClose}>
                    Error
                </ModalHeader>
                <ModalBody>
                    <Alert color="danger">{error}</Alert>
                </ModalBody>
            </Modal>
        );
    }

    if (!formLayout || !formValues) {
        return (
            <Modal isOpen={show} toggle={handleClose} size="xl">
                <ModalHeader toggle={handleClose}>
                    No Form Layout Found
                </ModalHeader>
                <ModalBody>
                    <Alert color="warning">No form layout was found for this property.</Alert>
                </ModalBody>
            </Modal>
        );
    }

    const hasBasicFields = formLayout.some(field => field.type !== "array" && field.type !== "group");

    return (
        <Modal
            isOpen={show}
            toggle={handleClose}
            style={{ maxWidth: '85vw', width: '85vw' }}
            className="modal-xxl"
        >
            <ModalHeader toggle={handleClose} className="justify-content-center">
                {buildTenantInfoString(tenantInfo)}
            </ModalHeader>
            <ModalBody>
                <div className="container-fluid">
                    <AccordionContext.Provider value={{ openGroups, toggleGroup }}>
                        <Form>
                            {formLayout
                                .filter(field => field.type === "array" || field.type === "group")
                                .map((field, index) => (
                                    <Col md={12} key={index} className="mb-4 px-0">
                                        <DynamicField
                                            field={field}
                                            name={field.key}
                                            values={formValues}
                                        />
                                    </Col>
                                ))}

                            {hasBasicFields && (
                                <Card className="mb-4">
                                    <CardHeader className="py-3 text-center">
                                        <Button
                                            color="primary"
                                            outline
                                            className="fw-bold px-4 py-2 rounded-pill"
                                            onClick={() => toggleGroup('basicFields')}
                                        >
                                            <FeatherIcon icon="user" className="me-2" size={16} />
                                            Tenant Basic Information
                                        </Button>
                                    </CardHeader>
                                    <Collapse isOpen={openGroups['basicFields'] !== false}>
                                        <CardBody>
                                            <Row>
                                                {formLayout
                                                    .filter(field => field.type !== "array" && field.type !== "group")
                                                    .map((field, index) => (
                                                        <Col md={field.key === "status" || field.key === "comments" ? 6 : 3} key={index}>
                                                            <DynamicField
                                                                field={field}
                                                                name={field.key}
                                                                values={formValues}
                                                            />
                                                        </Col>
                                                    ))}
                                            </Row>
                                        </CardBody>
                                    </Collapse>
                                </Card>
                            )}

                            <MetaFields values={previewData} openGroups={openGroups} toggleGroup={toggleGroup} />
                            <Leases propertyId={propertyId} buildingName={buildingName} unitNo={tenantInfo?.unitNo || formValues?.unitNo || ''} />
                            <PadInfo
                                archivedItemId={previewData?._id || ''}
                                unitNo={tenantInfo?.unitNo || formValues?.unitNo || ''}
                                buildingName={buildingName}
                                propertyName={propertyName}
                                readOnly
                                canWrite={false}
                            />
                            <Documents documents={previewData?.documents || []} documentsNA={previewData?.documentsNA || {}} />
                            <Pictures pictures={previewData?.pictures || []} />
                            <Memos archivedItemId={previewData?._id || ''} />

                            <div className="mt-4 d-flex justify-content-end">
                                <Button
                                    color="secondary"
                                    onClick={handleClose}
                                >
                                    Close
                                </Button>
                            </div>
                        </Form>
                    </AccordionContext.Provider>
                </div>
            </ModalBody>
        </Modal>
    );
};

export { PreviewTenantForm };
